import { Alert, Form, Input, Modal, Radio, Select, Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import { useLazyGetUserSocialIntegrationsQuery } from '@app/api/SocialIntegrations';
import { ERROR_REQUIRED_FIELD, PREFIX } from '@app/constants';
import { useMemoizeObject, useUserPrint } from '@app/hooks';
import type { NLeads, NSmallGlobalEntities } from '@app/types';

import { defineAllIntegrationsExistence } from './defineAllIntegrationsExistence';
import { useWriteFirstModal } from './useWriteFirstModal';
import {
  CANCEL_BUTTON_LABEL,
  SAVE_BUTTON_LABEL,
} from './writeFirstModal.constants';
import { WriteFirstModalSimilarCustomer } from './WriteFirstModalSimilarCustomer';

import type { FC } from 'react';

import './WriteFirstModal.scss';

const { Text } = Typography;
const ruleIsRequire = [
  {
    required: true,
    message: ERROR_REQUIRED_FIELD,
  },
];
const WAREHOUSE_1C_LABEL = 'warehouse-1c';

interface WriteFirstModalProps {
  isWriteFirstModalOpen: boolean;
  onCancel: () => void;
  userSocialIntegrations?: NSmallGlobalEntities.ModelItem[];
  leads: NLeads.ModelLead[] | null;
}

export const WriteFirstModal: FC<WriteFirstModalProps> = ({
  isWriteFirstModalOpen,
  onCancel,
  userSocialIntegrations: initialUserSocialIntegrations,
  leads,
}) => {
  const { activeBranchId } = useUserPrint();
  const {
    finishForm,
    handleCancel,
    form,
    findDuplicate,
    similarCustomers,
    handleSimilarCustomersChange,
    similarCustomersSelectedValue,
    isNewClient,
    handleNewClientCheckboxClick,
    submit,
    isLoading,
  } = useWriteFirstModal({ onCancel });

  const [userSocialIntegrations, setUserSocialIntegrations] = useState<
    NSmallGlobalEntities.ModelItem[]
  >(initialUserSocialIntegrations || []);

  const memoIntegrations = useMemoizeObject(userSocialIntegrations);
  const [getUserSocialIntegrations] = useLazyGetUserSocialIntegrationsQuery();
  const selectOptions = useMemo(
    () =>
      userSocialIntegrations
        .filter(({ IdS }) => IdS !== WAREHOUSE_1C_LABEL)
        .map((item) => ({
          label: item.Name,
          value: item.Id,
        })),
    [userSocialIntegrations]
  );

  const getIntegrations = useCallback(() => {
    const areAllIntegrationsExisted = defineAllIntegrationsExistence(
      leads,
      memoIntegrations
    );
    const areMemoizedIntegrations = memoIntegrations?.length === 0;

    const startIntegrationsGetting = async () => {
      const integrationsResult = await getUserSocialIntegrations({
        ActiveBranchId: activeBranchId,
      }).unwrap();
      setUserSocialIntegrations(integrationsResult);
    };

    if (areMemoizedIntegrations || !areAllIntegrationsExisted) {
      startIntegrationsGetting();
    }
  }, [activeBranchId, getUserSocialIntegrations, leads, memoIntegrations]);

  return (
    <Modal
      className={`${PREFIX}write-first-modal`}
      open={isWriteFirstModalOpen}
      title="Написать первым"
      okText={SAVE_BUTTON_LABEL}
      cancelText={CANCEL_BUTTON_LABEL}
      onOk={submit}
      onCancel={() => {
        handleCancel();
      }}
      okButtonProps={{
        loading: isLoading,
      }}
      cancelButtonProps={{
        disabled: isLoading,
      }}
      afterOpenChange={getIntegrations}
    >
      <Form form={form} layout="vertical" onFinish={finishForm}>
        <div className="container">
          <div className="item-container">
            <Text strong className="label-require">
              От кого
            </Text>
            <Form.Item name="integrationId" rules={ruleIsRequire}>
              <Select
                showSearch
                className="employee-select"
                size="large"
                options={selectOptions}
                onChange={findDuplicate}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase()) ||
                  false
                }
              />
            </Form.Item>
          </div>

          <div className="row-container">
            <div className="item-container">
              <Text strong className="label-require">
                Кому
              </Text>
              <Form.Item name="userName" rules={ruleIsRequire}>
                <Input size="large" onChange={findDuplicate} />
              </Form.Item>
            </div>

            <div className="item-container">
              <Text strong className="label">
                Имя клиента
              </Text>
              <Form.Item name="clientName">
                <Input size="large" onChange={findDuplicate} />
              </Form.Item>
            </div>
          </div>
        </div>

        {!similarCustomers.length && (
          <Form.Item
            className="new-customer-form-item gray-round-border"
            name="isNewClient"
          >
            <Radio
              defaultChecked
              checked={isNewClient}
              onChange={handleNewClientCheckboxClick}
            >
              Новый клиент
            </Radio>
          </Form.Item>
        )}

        {similarCustomers.length ? (
          <Alert
            className="similar-customers-warning"
            message="Похожие клиенты"
            type="warning"
            showIcon
          />
        ) : (
          ''
        )}

        <div>
          <Radio.Group
            onChange={handleSimilarCustomersChange}
            value={similarCustomersSelectedValue}
            className="similar-customers"
          >
            {similarCustomers.map(
              (
                {
                  NameFirst,
                  NameLast,
                  Id,
                  DateBirth,
                  Phone1,
                  Email,
                  IsPhoneVerified,
                  Branch,
                },
                index
              ) => (
                <WriteFirstModalSimilarCustomer
                  key={index}
                  name={`${NameLast || ''} ${NameFirst}`}
                  birthDate={DateBirth}
                  phone={Phone1}
                  email={Email}
                  isPhoneVerified={IsPhoneVerified}
                  branches={Branch}
                  id={Id}
                />
              )
            )}
          </Radio.Group>
        </div>
      </Form>
    </Modal>
  );
};
